import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { InvoiceMetadataDisplayLogic } from './logic'
import { TextField } from '../../../../firebase-functions/src/common/components/text-field'
import type { InvoiceMetadataDisplayProps } from '~/features/ui-components/invoice-metadata-display/types'
import { formatTimestampForDisplay } from '~common/utils/date-transformation/date-transformations'
import { useCacheValue } from '~/cache-context'
import { LoadingIndicator } from '~common/components/loading'
import { Icon } from '~common/components/icon'
import { Button } from '~common/components/button'
import { DatePicker } from '../date-picker'

export const InvoiceMetadataDisplay: React.FC<InvoiceMetadataDisplayProps> = (
    props,
) => {
    const { logic, state } = useLogic(InvoiceMetadataDisplayLogic, props)
    const settings = useCacheValue('settings')
    const [showSavedIndicator, setShowSavedIndicator] = useState(false)

    useEffect(() => {
        if (state.showUpdateMatches) {
            setShowSavedIndicator(true)
            const timer = setTimeout(() => {
                setShowSavedIndicator(false)
            }, 3000)
            return () => clearTimeout(timer)
        }
    }, [state.showUpdateMatches])

    if (state.loadState === 'running' && !state.metadata) {
        return (
            <Container>
                <LoadingContainer>
                    <LoadingIndicator />
                    <span>Loading invoice data...</span>
                </LoadingContainer>
            </Container>
        )
    }

    if (state.loadState === 'error' || state.error) {
        return (
            <Container>
                <ErrorContainer>
                    <ErrorText>
                        {state.error || 'Failed to load invoice data'}
                    </ErrorText>
                </ErrorContainer>
            </Container>
        )
    }

    if (!state.metadata) {
        return (
            <Container>
                <NoMetadata>No invoice metadata available</NoMetadata>
            </Container>
        )
    }

    const metadata = state.metadata

    const dateFormat = settings?.dateFormat ?? 'DD/MM/YYYY'
    const isSaving = state.loadState === 'running'

    return (
        <Container>
            <HeaderContainer>
                <HeaderLeft>
                    <Icon name={'document'} iconColor={'info'} />
                    <HeaderTitle>Invoice Metadata</HeaderTitle>
                </HeaderLeft>
                <HeaderRight>
                    {isSaving && (
                        <SavedIndicator>
                            <LoadingIndicator size="small" />
                            <SavedText>Saving...</SavedText>
                        </SavedIndicator>
                    )}
                    {showSavedIndicator && !isSaving && (
                        <SavedIndicator>
                            <Icon name="check-circle" iconColor="success" />
                            <SavedText>AutoSaved</SavedText>
                        </SavedIndicator>
                    )}
                    {state.showUpdateMatches && (
                        <Button
                            label="Update Matches"
                            variant="secondary"
                            icon="magic"
                            size="small"
                            onClick={async () => {
                                await logic.updateMatches()
                            }}
                            disabled={state.updateMatchesState === 'running'}
                        />
                    )}
                </HeaderRight>
            </HeaderContainer>
            <MetadataGrid>
                <TextField
                    label="Vendor"
                    icon="user"
                    initValue={metadata.vendorName || ''}
                    editable={true}
                    onConfirm={(value) =>
                        logic.updateField('vendorName', value)
                    }
                    confirmOnBlur={true}
                />

                <DatePicker
                    label="Due Date"
                    icon="calendar"
                    initValue={new Date(metadata.date)}
                    onConfirm={(value) => {
                        if (value instanceof Date) {
                            logic.updateField('date', String(value.getTime()))
                        } else {
                            logic.updateField('date', '')
                        }
                    }}
                    singleDate={true}
                />

                <TextField
                    icon={<CurrencyIcon>{metadata.currency}</CurrencyIcon>}
                    label="Gross Amount"
                    editable={true}
                    initValue={(metadata.amount ?? 0).toString()}
                    onConfirm={(value) => logic.updateField('amount', value)}
                    inputValidation={['numbersOnly']}
                    confirmOnBlur={true}
                />
                <TextField
                    label="What's been bought"
                    initValue={metadata.reason || ''}
                    multiline={true}
                    editable={true}
                    onConfirm={(value) => logic.updateField('reason', value)}
                    confirmOnBlur={true}
                />

                <TextField
                    label="Address"
                    initValue={metadata.vendorAddress || ''}
                    multiline={true}
                    editable={true}
                    onConfirm={(value) =>
                        logic.updateField('vendorAddress', value)
                    }
                    confirmOnBlur={true}
                />
                <TextField
                    label="Country"
                    initValue={metadata.vendorCountry || ''}
                    editable={true}
                    onConfirm={(value) =>
                        logic.updateField('vendorCountry', value)
                    }
                    confirmOnBlur={true}
                />

                <TextField
                    label="Invoice ID"
                    initValue={metadata.invoiceId || ''}
                    editable={true}
                    onConfirm={(value) => logic.updateField('invoiceId', value)}
                    confirmOnBlur={true}
                />

                <TextField
                    icon={<CurrencyIcon>%</CurrencyIcon>}
                    label="VAT Rate %"
                    editable={true}
                    initValue={
                        metadata.vatRate !== null ? `${metadata.vatRate}` : ''
                    }
                    onConfirm={(value) => logic.updateField('vatRate', value)}
                    inputValidation={['numbersOnly']}
                    confirmOnBlur={true}
                />

                <TextField
                    icon={<CurrencyIcon>{metadata.currency}</CurrencyIcon>}
                    initValue={
                        metadata.vatAmount !== null
                            ? `${metadata.vatAmount}`
                            : ''
                    }
                    label="VAT Amount"
                    editable={true}
                    onConfirm={(value) => logic.updateField('vatAmount', value)}
                    inputValidation={['numbersOnly']}
                    confirmOnBlur={true}
                />

                {/* <TextField
                    label="Credit Card"
                    text={metadata.creditCard || 'N/A'}
                    editable={true}
                /> */}

                {/* {metadata.vendorEmailAddresses.length > 0 && (
                    <TextField label="Vendor Emails">
                        <div>
                            {metadata.vendorEmailAddresses.map(
                                (email: string, index: number) => (
                                    <EmailItem key={index}>{email}</EmailItem>
                                ),
                            )}
                        </div>
                    </TextField>
                )} */}
            </MetadataGrid>
        </Container>
    )
}

const Container = styled.div`
    box-sizing: border-box;
    padding: ${({ theme }) => theme.spacing[4]};
    background-color: ${({ theme }) => theme.colors.background.bg2};
    border-radius: ${({ theme }) => theme.borderRadius['2xl']};
`

const MetadataGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[4]};

    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 120px;
    gap: ${({ theme }) => theme.spacing[3]};
    color: ${({ theme }) => theme.colors.text.primary};
`

const ErrorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    padding: ${({ theme }) => theme.spacing[4]};
`

const ErrorText = styled.p`
    color: ${({ theme }) => theme.colors.error[600]};
    margin: 0;
    text-align: center;
`

const NoMetadata = styled.div`
    color: ${({ theme }) => theme.colors.text.tertiary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    text-align: center;
    padding: ${({ theme }) => theme.spacing[4]};
`

const EmailItem = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing[1]};

    &:last-child {
        margin-bottom: 0;
    }
`

const CurrencyIcon = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
`

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${({ theme }) => theme.spacing[3]};
    gap: ${({ theme }) => theme.spacing[3]};
    height: ${({ theme }) => theme.spacing[10]};
`

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    flex: 1;
`
const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing[6]};
    flex: 1;
`

const HeaderTitle = styled.h3`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
`

const SavedIndicator = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    margin-left: ${({ theme }) => theme.spacing[2]};
`

const SavedText = styled.span`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.secondary};
`
